import React, {
    useContext, useCallback, useEffect, useState
} from 'react';
import _ from 'lodash';
import { TranslatorContext } from '@jutro/locale';
import { BreakpointTrackerContext } from '@jutro/layout';
import { WizardPage, wizardProps } from 'gw-portals-wizard-react';
import { ViewModelServiceContext, ViewModelForm } from 'gw-portals-viewmodel-react';
import { GatewayDocumentService } from 'gw-capability-gateway-document';
import { useDependencies } from 'gw-portals-dependency-react';
import { useAuthentication } from 'gw-digital-auth-react';
import { useValidation } from 'gw-portals-validation-react';
import { ClausesUtil } from 'gw-policycommon-util-js';
import { MockUpUtil } from 'gw-portals-util-js';
import {SubmissionService} from 'gw-capability-gateway';
import { ModalNextProvider, Loader } from '@jutro/components';
// eslint-disable-next-line import/no-unresolved
import appConfig from 'app-config';
import styles from './QuotePage.module.scss';
import messages from './QuotePage.messages';

import metadata from './QuotePage.metadata.json5';

const PATH_TO_MOCK_DATA = 'quote.ba';
const MOCK_DATA_TO_SET = [
    'bindData.contactPhone',
    'bindData.contactEmail'
];
const BLOCKS_QUOTES = ['BlocksQuote', 'BlocksQuoteRelease'];
const BLOCKS_BIND = 'BlocksBind';

function structureClauseTableData(coverage, index = '') {
    // putting ID into an object as the Jutro table component expects an object
    return {
        coverageUniqueID: `${coverage.publicID}${index}`
    };
}

const formatPublicID = (publicID) => _.toInteger(publicID.replace(/pc:/, ''));

function generateClauseData(columnData, coveragePath, vehiclePublicID) {
    return columnData.map(({ lob, code }) => {
        let completeCoveragePath = `coverages.${coveragePath}`;

        if (vehiclePublicID) {
            const vehicleOfferings = lob.data.coverages.vehicleCoverages;
            const vehicleOfferingIndex = vehicleOfferings.findIndex(
                (vehicleCoverage) => vehicleCoverage.publicID === vehiclePublicID
            );
            completeCoveragePath = `coverages.${coveragePath}.children[${vehicleOfferingIndex}].coverages`;
        }

        return {
            code: code,
            path: `${lob.path}.${completeCoveragePath}`,
            clauses: _.get(lob.data, completeCoveragePath.replace(/\.children/, ''))
        };
    });
}

const structureCustomQuote = (submissionVM, affectedQuote, clauses) => {
    // convert OfferingDTO to CustomQuotedDTO structure
    return {
        quote: affectedQuote,
        quoteID: submissionVM.quoteID.value,
        sessionUUID: submissionVM.sessionUUID.value,
        periodStart: submissionVM.baseData.periodStartDate.value,
        periodEnd: submissionVM.baseData.periodEndDate.value,
        coverages: clauses,
        autoMultiLineDiscount: submissionVM.baseData.autoMultiLineDiscount.value,
        multiLineDiscount: submissionVM.baseData.multiLineDiscount.value,
        selectedPaymentPlanData: submissionVM.bindData.selectedPaymentPlan.value,
        binderPolicyNumber_EY: submissionVM.baseData.binderPolicyNumber_EY.value,
        noteForUW: submissionVM.baseData.noteForUW.value
    };
};

const getCoveragesUniqueID = (submissionVM) => {
    const offerings = _.get(submissionVM, 'lobData.businessAuto.offerings.value');
    const lineCoverages = _.uniqBy(
        offerings.flatMap((offering) => offering.coverages
            .lineCoverages.map((coverage) => structureClauseTableData(coverage))),
        'coverageUniqueID'
    );
    const vehicleCoverages = _.uniqBy(
        offerings.flatMap((offering) => offering.coverages
            .vehicleCoverages.flatMap(({ coverages }, index) => coverages
                .map((coverage) => structureClauseTableData(coverage, `__${index}`)))),
        'coverageUniqueID'
    );
    return {
        lineCoverages,
        vehicleCoverages
    };
};

const generateColumnData = (submissionVM) => {
    const lobOfferingPath = 'lobData.businessAuto.offerings';
    const quoteOfferingPath = 'quoteData.offeredQuotes';

    const lobOfferings = _.get(submissionVM, `${lobOfferingPath}.value`);
    const quoteOfferings = _.get(submissionVM, `${quoteOfferingPath}.value`) || [];

    const columnData = lobOfferings
        .map((lobOffering, lobIndex) => {
            const quoteDataIndex = quoteOfferings.findIndex(
                (qdOffering) => qdOffering.branchCode === lobOffering.branchCode
            );
            const quoteData = quoteOfferings[quoteDataIndex];
            return {
                name: lobOffering.branchName,
                code: lobOffering.branchCode,
                quote: {
                    path: `${quoteOfferingPath}.children.${quoteDataIndex}`,
                    data: quoteData
                },
                lob: {
                    path: `${lobOfferingPath}.children.${lobIndex}`,
                    data: lobOffering
                }
            };
        })
        .filter(({ code, quote }) => code !== 'CUSTOM' && !_.isUndefined(quote.data));
    return _.sortBy(columnData, ['code']);
};

const getCustomQuote = (vm, lobPath, quotePath, lobName, filterChangedClauses = false) => {
    const lobOffering = _.get(vm, `${lobPath}.value`);
    const quoteOffering = _.get(vm, `${quotePath}.value`);

    let clausesToUpdate = {
        [lobName]: lobOffering.coverages
    };

    if (filterChangedClauses) {
        // eslint-disable-next-line max-len
        clausesToUpdate = ClausesUtil.structureClausesForServer(
            lobOffering.coverages,
            lobName,
            null
        );
    }

    return structureCustomQuote(vm, quoteOffering, clausesToUpdate);
};

const generateTableData = (submissionVM, columnData, translator) => {
    const vehicles = _.get(submissionVM, 'lobData.businessAuto.coverables.vehicles.value');
    const uniqueID = getCoveragesUniqueID(submissionVM);

    /*const lineCoverages = {
        header: translator(messages.generalCoverages),
        data: uniqueID.lineCoverages,
        tableContent: generateClauseData(columnData, 'lineCoverages')
    };*/

    const vehicleCoverages = vehicles.map((vehicle) => ({
        header: `${vehicle.year} ${vehicle.make} ${vehicle.model}`,
        data: uniqueID.vehicleCoverages.filter(
            (coverage) => coverage.coverageUniqueID.endsWith(`__${vehicles.findIndex((section) => section === vehicle)}`)
        ),
        tableContent: generateClauseData(columnData, 'vehicleCoverages', vehicle.publicID)
    }));

    /*return [lineCoverages, ...vehicleCoverages];*/
    return [...vehicleCoverages];
};

function QuotePage(props) {
    const { wizardSnapshot, wizardData: submissionVM, updateWizardData, quoteData, updateQuotedData, additionalWizardData, updateAdditionalWizardData } = props;
    const translator = useContext(TranslatorContext);
    const breakpoint = useContext(BreakpointTrackerContext);
    const [staleQuoteBranchCode, setStaleQuoteBranchCode] = useState(undefined);
    const { CustomQuoteService } = useDependencies('CustomQuoteService');
    const { authHeader, checkPermission } = useAuthentication();
    const showQuoteStartDate = appConfig.showQuoteStartDateInHeader;
    const { onValidate, disregardFieldValidation } = useValidation('QuotePage');
    const viewModelService = useContext(ViewModelServiceContext);
    const DocumentPageService = {
        downloadDocument: GatewayDocumentService.downloadDocument
    };
    const [initialSubmissionVM, setInitialSubmissionVM] = useState(undefined);
    const [isLoading, setLoading] = useState(false);
    const [isLoader, setLoader] = useState(false);
    const [isRecalculating, setisRecalculating] = useState(false);

    const onClauseChange = useCallback(
        (_basePath, lobPath, quotePath) => {
            setLoader(true);
            const lobName = ClausesUtil.getLobNameFromPath(lobPath);
            const customQuote = getCustomQuote(submissionVM, lobPath, quotePath, lobName, true);
            return CustomQuoteService.updateCustomQuoteCoverages(customQuote, authHeader).then(
                (response) => {
                    const updatedClauses = _.get(response, `coverages.${lobName}`);
                    const newSubmissionVM = viewModelService.clone(submissionVM);
                    // Update the offering status to stale
                    setStaleQuoteBranchCode(response.quote.branchCode);
                    // Update local offering with new one from xcenter
                    _.set(submissionVM, `${lobPath}.coverages`, updatedClauses);
                    // Update local quote status with new one from xcenter
                    const status = _.get(response, 'quote.status', 'Draft');
                    _.set(submissionVM, `${quotePath}.status`, status);
                    // Update local errorsAndWarnings with new one from xcenter
                    _.set(submissionVM, 'errorsAndWarnings', response.errorsAndWarnings);
                    // Update premium with new one from xcenter
                    _.set(submissionVM, `${quotePath}.premium`, response.quote.premium);
                    const removedFieldsFromBaseCoverages = ClausesUtil.getRemovedClausesID(
                        submissionVM,
                        newSubmissionVM,
                        `${lobPath}.coverages.lineCoverages`
                    );
                    const removedFieldsFromAdditionalCoverages = ClausesUtil.getRemovedClausesID(
                        submissionVM,
                        newSubmissionVM,
                        `${lobPath}.coverages.vehicleCoverages`
                    );
                    const allRemovedFields = [
                        ...removedFieldsFromBaseCoverages,
                        ...removedFieldsFromAdditionalCoverages
                    ];
                    disregardFieldValidation(allRemovedFields);
                    updateWizardData(submissionVM);
                    setLoader(false);
                }
            );
        },
        [
            submissionVM,
            CustomQuoteService,
            authHeader,
            viewModelService,
            disregardFieldValidation,
            updateWizardData,
            setLoader
        ]
    );

    const syncCoverages = useCallback(
        (value, changedPath, lobPath, quotePath) => {
            const basePath = ClausesUtil.getObjectPathFromChangedPath(changedPath);
            return onClauseChange(basePath, lobPath, quotePath);
        },
        [onClauseChange]
    );

    const changeSubmission = useCallback(
        (value, changedPath) => {
            updateWizardData(ClausesUtil.setClauseValue(submissionVM, value, changedPath));
        },
        [submissionVM, updateWizardData]
    );

    const changeAutoMultilineDiscount = useCallback(
        (value, changedPath) => {
            const newSubmissionVM = viewModelService.clone(submissionVM);
            _.set(newSubmissionVM.value, 'baseData.autoMultiLineDiscount', value);
            updateWizardData(newSubmissionVM);
            /*_.set(submissionVM, `baseData.${changedPath}`, value);
            updateWizardData(submissionVM);*/
        },
        [submissionVM, updateWizardData]
    );

    const changeMultilineDiscount = useCallback(
        (value, changedPath) => {
            const newSubmissionVM = viewModelService.clone(submissionVM);
            _.set(newSubmissionVM.value, 'baseData.multiLineDiscount', value);
            updateWizardData(newSubmissionVM);
            /*_.set(submissionVM, `baseData.${changedPath}`, value);
            updateWizardData(submissionVM);*/
        },
        [submissionVM, updateWizardData]
    );

    const changeBinderPolicy = useCallback(
        (value, changedPath) => {
            const newSubmissionVM = viewModelService.clone(submissionVM);
            _.set(newSubmissionVM.value, 'baseData.binderPolicyNumber_EY', value);
            updateWizardData(newSubmissionVM);
        },
        [submissionVM, updateWizardData]
    );

    const changeNoteForUW = useCallback(
        (value, changedPath) => {
            const newSubmissionVM = viewModelService.clone(submissionVM);
            _.set(newSubmissionVM.value, 'baseData.noteForUW', value);
            updateWizardData(newSubmissionVM);
        },
        [submissionVM, updateWizardData]
    );

    const changePaymentPlanFrequency = useCallback(
        (value, changedPath) => {
            const newSubmissionVM = viewModelService.clone(submissionVM);
            _.set(newSubmissionVM.value, 'bindData.selectedPaymentPlan', value);
            updateWizardData(newSubmissionVM);
           /* _.set(submissionVM, 'baseData.selectedPaymentPlanData.value', value);
             updateWizardData(submissionVM);*/
        },
        [submissionVM, updateWizardData]
    );

    const changeSubmissionAndSync = useCallback(
        (value, changedPath, lobPath, quotePath) => {
            changeSubmission(value, changedPath);
            return syncCoverages(value, changedPath, lobPath, quotePath);
        },
        [changeSubmission, syncCoverages]
    );

    const resetQuote = useCallback(
        (lobPath, quotePath) => {
            const lobName = ClausesUtil.getLobNameFromPath(lobPath);
            const customQuote = getCustomQuote(initialSubmissionVM, lobPath, quotePath, lobName);

            return CustomQuoteService.forceUpdateCustomQuoteCoverages(customQuote, authHeader).then(
                (response) => {
                    const updatedClauses = _.get(response, `coverages.${lobName}`);
                    // Update local offering with new one from xcenter
                    _.set(submissionVM, `${lobPath}.coverages`, updatedClauses);
                    // Update local quote status with new one from xcenter
                    const status = _.get(response, 'quote.status', 'Draft');
                    _.set(submissionVM, `${quotePath}.status`, status);
                    // Update local errorsAndWarnings with new one from xcenter
                    _.set(submissionVM, 'errorsAndWarnings', response.errorsAndWarnings);
                    // Update premium with new one from xcenter
                    _.set(submissionVM, `${quotePath}.premium`, response.quote.premium);
                    const newSubmissionVM = viewModelService.clone(submissionVM);
                    updateWizardData(newSubmissionVM);
                }
            );
        },
        [initialSubmissionVM, submissionVM, updateWizardData, CustomQuoteService, authHeader]
    );

    const recalculate = useCallback(
        (lobPath, quotePath) => {
            const lobName = ClausesUtil.getLobNameFromPath(lobPath);
            const customQuote = getCustomQuote(submissionVM, lobPath, quotePath, lobName);
            let monthlyPaymentId = submissionVM.bindData.paymentPlans.value ? submissionVM.bindData.paymentPlans.value.filter((i)=>{return i.name == "Monthly Plan"}) : []
            monthlyPaymentId = monthlyPaymentId.length > 0 ? monthlyPaymentId[0].billingId : '';
            setisRecalculating(true)
            return CustomQuoteService.updateCustomQuote(customQuote, authHeader).then(
                (response) => {
                    setisRecalculating(false);
                    const _additionalWizardData = _.clone(additionalWizardData);
                    _additionalWizardData.showError = true;
                    updateAdditionalWizardData(_additionalWizardData);
                    setStaleQuoteBranchCode(undefined);
                    const updatedClauses = _.get(response, `coverages.${lobName}`);
                    // Update local offering with new one from xcenter
                    _.set(submissionVM, `${lobPath}.coverages`, updatedClauses);
                    const status = _.get(response, 'quote.status', 'Draft');
                    _.set(submissionVM, `${quotePath}.status`, status);
                    // Update local errorsAndWarnings with new one from xcenter
                    _.set(submissionVM, 'errorsAndWarnings', response.errorsAndWarnings);
                    // Update premium with new one from xcenter
                    _.set(submissionVM, `${quotePath}.premium`, response.quote.premium);

                    if( submissionVM.bindData.selectedPaymentPlan && submissionVM.bindData.selectedPaymentPlan.value == monthlyPaymentId ){
                        _.set(submissionVM, `bindData.paymentDetails.billingMethod_EY`, 'Direct Bill');  
                        _.set(submissionVM, `bindData..billingMethod_EY`, 'Direct Bill'); 

                    }
                    const newSubmissionVM = viewModelService.clone(submissionVM);
                    updateWizardData(newSubmissionVM);
                    return response;
                }, () => {setisRecalculating(false)}
            );
        },
        [submissionVM, updateWizardData, CustomQuoteService, authHeader, setisRecalculating]
    );

    const generateQuoteDocument = useCallback(
        () => {
        ModalNextProvider.showConfirm({
              title: messages.baGenerateQuoteDocument,
              message: messages.baGenerateQuoteDocumentConf,
              status: 'warning',
              icon: 'mi-error-outline'
          }).then((results) => {
            if (results === 'cancel') {
                return _.noop();
            }
            setLoading(true);
            SubmissionService.generateQuoteDocumentPortal(submissionVM.value, submissionVM.baseData.isIssuanceJob.value, authHeader).then((response) => {
                setLoading(false);
                if(response.publicID && response.sessionID){
                    var downloadUrl = DocumentPageService.downloadDocument(
                        response.publicID,
                        response.sessionID
                    );
                    window.open(downloadUrl, '_blank');
                } 
                else{
                    ModalNextProvider.showAlert({
                        title: 'Unable to generate quote document',
                        message: 'Problem occured while generating quote document. Please try again later',
                        status: 'error',
                        icon: 'mi-error-outline',
                        confirmButtonText: 'Ok'
                    }).catch(_.noop);
                }
            }).catch(() => {
                setLoading(false);
                /*ModalNextProvider.showAlert({
                    title: 'Unable to generate quote document',
                    message: 'Problem occured while generating quote document. Please try again later',
                    status: 'error',
                    icon: 'mi-error-outline',
                    confirmButtonText: 'Ok'
                }).catch(_.noop);*/
            });
        }, _.noop);
        },
        [submissionVM, authHeader]
    );

    const handlePrint = useCallback(() => {
        window.print();
    }, []);

    const buyNow = useCallback(
        async (lobPath, quotePath) => {
            const _additionalWizardData = _.clone(additionalWizardData);
            _additionalWizardData.showError = true;
            updateAdditionalWizardData(_additionalWizardData);
            const lobOffering = _.get(submissionVM, `${lobPath}.value`);
            const quoteOffering = _.get(submissionVM, `${quotePath}.value`);
            const quoteID = _.get(submissionVM, 'quoteID.value');
            const sessionUUID = _.get(submissionVM, 'sessionUUID.value');

            await CustomQuoteService.setSelectedVersionOnSubmission(
                quoteID,
                lobOffering.branchName,
                sessionUUID,
                authHeader
            );

            // set chosen quote
            _.set(submissionVM, 'bindData.chosenQuote.value', quoteOffering.publicID);
            updateWizardData(submissionVM);
        },
        [submissionVM, updateWizardData, CustomQuoteService, authHeader]
    );

    const generateOverrides = useCallback(() => {
        const columnData = generateColumnData(submissionVM);
        const { quoteID } = submissionVM;
        const { underwritingIssues } = props;

        return {
            '@field': {
                // apply to all fields
                labelPosition: breakpoint === 'desktop' ? 'left' : 'top'
            },
            quoteTable: {
                columnData: columnData,
                tableData: generateTableData(submissionVM, columnData, translator),
                underwritingIssues: underwritingIssues,
                quoteID: quoteID.value,
                baseData: submissionVM.baseData.value,
                bindData: submissionVM.bindData.value,
                isQuoted: quoteData.isQuoted,
                updateQuotedData: (value) => {
                    updateQuotedData(value);
                    updateWizardData(submissionVM);
                },
                submissionVM: submissionVM,
                disableRecalculate: isRecalculating || isLoading || isLoader,
                additionalWizardData,
                updateAdditionalWizardData
            },
            quoteContainer:{
                className: isLoader ? 'disable' : ''
            },
            startDate: {
                visible: showQuoteStartDate
            },
            printPage:{
                visible: checkPermission('genquotesummarydoc_EY') && !showQuoteStartDate,
                disabled: !(submissionVM.baseData && submissionVM.baseData.periodStatus && submissionVM.baseData.periodStatus.value.code === 'Quoted')
            }
        };
        // prevent complaining props and showQuoteStartDate is missing in the dependencies list
        // watch props will cause refresh function ref multiple time,
        // showQuoteStartDate is generated from build time, so won't change in the runtime
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [breakpoint, submissionVM, translator, isLoader, isRecalculating]);

    useEffect(() => {
        const offerings = _.get(submissionVM, 'quoteData.offeredQuotes.value');
        const draftOffers = _.filter(offerings, ['status', 'Draft']);
        const draftBranchCodes = _.map(draftOffers, 'branchCode');
        setStaleQuoteBranchCode(draftBranchCodes);
        // Store the initial SubmissionVM when component is mounted
        setInitialSubmissionVM(viewModelService.clone(wizardSnapshot));

        // inject mock data to prevent wizardData.subtreeValid is invalid
        submissionVM.value = MockUpUtil.setMockData(
            submissionVM.value,
            PATH_TO_MOCK_DATA,
            ...MOCK_DATA_TO_SET
        );

        // Above logic only needs to run once when component is mounted
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);


    const onStaleQuoteBranchCode = useCallback(() => {
        return staleQuoteBranchCode;
    }, [staleQuoteBranchCode]);

    if (isLoading) {
        return <Loader loaded={!isLoading} />;
    }

    const isBlocksQuote = submissionVM.errorsAndWarnings.underwritingIssues.value && submissionVM.errorsAndWarnings.underwritingIssues.value.some((offeringIssue) => {
            return offeringIssue.currentBlockingPoint === "BlocksQuote";            
        });
    
    const isBlocksQuoteRelease = submissionVM.errorsAndWarnings.underwritingIssues.value && submissionVM.errorsAndWarnings.underwritingIssues.value.some((offeringIssue) => {
            return offeringIssue.currentBlockingPoint === "BlocksQuoteRelease";            
        });

    const hasQuotableValidationIssues = submissionVM.errorsAndWarnings.validationIssues.value.issues && submissionVM.errorsAndWarnings.validationIssues.value.issues.length > 0 && submissionVM.errorsAndWarnings.validationIssues.value.level == "quotable"

    return (
        <WizardPage showNext={isBlocksQuote || isBlocksQuoteRelease} 
                    showPrevious={false} 
                    showCancel={false}
                    disableNext={hasQuotableValidationIssues}>
            {({ onNext }) => {
                const resolvers = {
                    resolveClassNameMap: styles,
                    resolveCallbackMap: {
                        onBuyNow: (lobPath, quotePath) => buyNow(lobPath, quotePath).then(onNext),
                        onStaleQuoteBranchCode: onStaleQuoteBranchCode,
                        onRecalculate: recalculate,
                        onResetQuote: resetQuote,
                        onChangeSubmissionAndSync: changeSubmissionAndSync,
                        onChangeSubmission: changeSubmission,
                        onSyncCoverages: syncCoverages,
                        onPrint: handlePrint,
                        onChangeAutoMultilineDiscount: changeAutoMultilineDiscount,
                        onChangeMultilineDiscount: changeMultilineDiscount,
                        onChangePaymentPlanFrequency: changePaymentPlanFrequency,
                        generateQuoteDocument: generateQuoteDocument,
                        onChangeBinderPolicy: changeBinderPolicy,
                        onChangeNoteForUW: changeNoteForUW
                    }
                };

                return (
                    <ViewModelForm
                        uiProps={metadata.pageContent}
                        model={submissionVM}
                        overrideProps={generateOverrides()}
                        onModelChange={updateWizardData}
                        classNameMap={resolvers.resolveClassNameMap}
                        callbackMap={resolvers.resolveCallbackMap}
                        onValidationChange={onValidate}
                    />
                );
            }}
        </WizardPage>
    );
}

QuotePage.propTypes = wizardProps;
export default QuotePage;
